import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, 
  Users, 
  Phone, 
  DollarSign,
  TrendingUp,
  Ban,
  CheckCircle,
  Search,
  MoreVertical,
  Crown,
  AlertTriangle,
  AlertCircle,
  Bell,
  Activity,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState(null);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("speakr_admin_token")}`
  });

  useEffect(() => {
    fetchData();
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const [activeRes, statsRes] = await Promise.all([
        axios.get(`${API}/v1/alerts/active`),
        axios.get(`${API}/v1/alerts/stats`)
      ]);
      // Combine firing and acknowledged alerts for display
      const firingAlerts = activeRes.data.firing || [];
      const acknowledgedAlerts = activeRes.data.acknowledged || [];
      const allActiveAlerts = [...firingAlerts, ...acknowledgedAlerts];
      setAlerts(allActiveAlerts.slice(0, 5));
      setAlertStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load alerts:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, bizRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/businesses`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setBusinesses(bizRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin");
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSubscription = async (businessId, action) => {
    try {
      if (action === "suspend") {
        await axios.put(`${API}/admin/businesses/${businessId}/suspend`, {}, { headers: getAuthHeader() });
        toast.success("Business suspended");
      } else if (action === "activate") {
        await axios.put(`${API}/admin/businesses/${businessId}/activate`, {}, { headers: getAuthHeader() });
        toast.success("Business activated");
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to update business");
    }
  };

  const changePlan = async (businessId, plan) => {
    try {
      const formData = new FormData();
      formData.append("plan", plan);
      await axios.put(`${API}/admin/businesses/${businessId}/subscription`, formData, { headers: getAuthHeader() });
      toast.success("Plan updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update plan");
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case "enterprise": return "bg-gradient-to-r from-amber-500 to-orange-500 text-white";
      case "professional": return "bg-gradient-to-r from-purple-500 to-pink-500 text-white";
      case "starter": return "bg-gradient-to-r from-blue-500 to-cyan-500 text-white";
      default: return "bg-emerald-500/20 text-emerald-400";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-500/20 text-emerald-400";
      case "suspended": return "bg-red-500/20 text-red-400";
      case "cancelled": return "bg-gray-500/20 text-gray-400";
      default: return "bg-blue-500/20 text-blue-400";
    }
  };

  const filteredBusinesses = businesses.filter(biz => {
    const matchesSearch = !searchQuery || 
      biz.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      biz.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || biz.subscription_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const logout = () => {
    localStorage.removeItem("speakr_admin_token");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-dots flex gap-1">
          <span className="w-3 h-3 bg-amber-500 rounded-full animate-bounce"></span>
          <span className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
          <span className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">GSK Productions • Speakr Platform Management</p>
        </div>
        <Button variant="outline" onClick={logout}>Logout</Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                <p className="text-3xl font-bold text-emerald-400">${stats?.monthly_revenue || 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Businesses</p>
                <p className="text-3xl font-bold">{stats?.total_businesses || 0}</p>
                <p className="text-xs text-emerald-400">+{stats?.recent_signups_7d || 0} this week</p>
              </div>
              <Building2 className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active / Suspended</p>
                <p className="text-3xl font-bold">
                  <span className="text-emerald-400">{stats?.active_businesses || 0}</span>
                  {" / "}
                  <span className="text-red-400">{stats?.suspended_businesses || 0}</span>
                </p>
              </div>
              <Users className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="text-3xl font-bold">{stats?.total_calls || 0}</p>
                <p className="text-xs text-muted-foreground">{stats?.total_leads || 0} leads generated</p>
              </div>
              <Phone className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Alerts Widget */}
      <Card className="mb-8 bg-card border-border" data-testid="alerts-widget">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              <CardTitle className="text-lg">Active Alerts</CardTitle>
              {alertStats?.by_status?.firing > 0 && (
                <Badge className="bg-red-500/20 text-red-400">
                  {alertStats.by_status.firing} firing
                </Badge>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/admin/system-monitor")}
              className="text-muted-foreground hover:text-foreground"
            >
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Activity className="w-5 h-5 mr-2" />
              <span>All systems operational - No active alerts</span>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert, idx) => (
                <div 
                  key={alert.id || idx}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    alert.severity === "critical" 
                      ? "bg-red-500/10 border-red-500/30" 
                      : alert.severity === "high"
                      ? "bg-orange-500/10 border-orange-500/30"
                      : "bg-amber-500/10 border-amber-500/30"
                  }`}
                  data-testid={`alert-item-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    {alert.severity === "critical" ? (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{alert.title || alert.rule_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {alert.message?.substring(0, 80) || "Check system status for details"}
                        {alert.message?.length > 80 ? "..." : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      alert.severity === "critical" 
                        ? "bg-red-500/20 text-red-400" 
                        : alert.severity === "high"
                        ? "bg-orange-500/20 text-orange-400"
                        : "bg-amber-500/20 text-amber-400"
                    }>
                      {alert.severity}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {alert.fired_at ? new Date(alert.fired_at).toLocaleTimeString() : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Distribution */}
      <Card className="mb-8 bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">Plan Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(stats?.plan_distribution || {}).map(([plan, count]) => (
              <div key={plan} className="text-center p-4 rounded-lg bg-secondary/50">
                <Badge className={getPlanColor(plan)}>{plan.replace("_", " ")}</Badge>
                <p className="text-3xl font-bold mt-2">{count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Businesses Table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">All Businesses</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  className="pl-9 w-48"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Agents</TableHead>
                  <TableHead>Calls</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBusinesses.map((biz) => (
                  <TableRow key={biz.id} data-testid={`business-row-${biz.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{biz.name}</p>
                        <p className="text-xs text-muted-foreground">{biz.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{biz.industry?.replace("_", " ")}</TableCell>
                    <TableCell>
                      <Badge className={getPlanColor(biz.subscription_plan)}>
                        <Crown className="w-3 h-3 mr-1" />
                        {biz.subscription_plan?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(biz.subscription_status)}>
                        {biz.subscription_status}
                      </Badge>
                    </TableCell>
                    <TableCell>{biz.agents_count}</TableCell>
                    <TableCell>{biz.calls_count}</TableCell>
                    <TableCell>{biz.contacts_count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(biz.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => changePlan(biz.id, "starter")}>
                            Set Starter Plan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => changePlan(biz.id, "professional")}>
                            Set Professional Plan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => changePlan(biz.id, "enterprise")}>
                            Set Enterprise Plan
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {biz.subscription_status === "active" ? (
                            <DropdownMenuItem 
                              onClick={() => updateSubscription(biz.id, "suspend")}
                              className="text-red-500"
                            >
                              <Ban className="w-4 h-4 mr-2" />
                              Suspend Account
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => updateSubscription(biz.id, "activate")}
                              className="text-emerald-500"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate Account
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
